/**
 * Commercial Position (PnL) dashboard — `/dashboard/pnl`.
 *
 * UI dựng theo `plan/PBI_Dashboard.md` (Power BI → web): HEADER + GRID 3 cột bảng
 * tài chính, mật độ cao, định dạng số/tiền chuẩn (số âm ngoặc + đỏ, $ prefix).
 * Cấu trúc bám PROJECT (MUI + sx, co-locate `pages/dashboard/pnl/`), KHÔNG Tailwind/
 * tanstack như doc gợi ý.
 *
 * DATA: đọc từ Report DB qua `useGetPnLDashboardQuery` (GET /api/v1/dashboard/pnl).
 * BE chỉ trả ROWS; column/format config vẫn lấy từ `mockData.ts`. Khi SQL Report DB
 * chưa điền (rows rỗng), card fallback về số dummy của `mockData.ts` để vẫn render.
 */
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button, CircularProgress, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { usePreference } from '../../../hooks/usePreference'
import { useGetPnLDashboardQuery } from '../../../store/api/dashboardPnlApi'
import { FILTER_HEADER_INPUT_SX } from '../../../theme/responsive'
import PnLTable, { type PnLRow } from './PnLTable'
import { PNL_COLORS } from './tokens'
import {
  exchangeRateTable,
  futuresTable,
  inventoryTable,
  longShortTable,
  m2mPnlTable,
  marketPriceTable,
  physicalPriceTable,
  purchasesTable,
  salesTable,
  supplierM2MTable,
  type PnLTableData,
} from './mockData'

/** Card trắng + tiêu đề xanh đậm (doc §2, §7). */
function PnLCard({
  title,
  unit,
  data,
  height,
  highlight,
  loading,
}: {
  title: string
  unit?: string
  data: PnLTableData
  /** Height bảng cố định (px) — bảng dài scroll + sticky header; các block cùng
   *  hàng grid set cùng height để card đều nhau. */
  height?: number
  highlight?: (row: PnLRow, colKey: string) => string | undefined
  /** Reload theo từng block: progress bar + mờ bảng RIÊNG card này khi refetch. */
  loading?: boolean
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 0.75, borderColor: PNL_COLORS.border, borderRadius: 1.5, bgcolor: PNL_COLORS.rowBg }}
    >
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: PNL_COLORS.titleText, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        {unit ? (
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{unit}</Typography>
        ) : null}
      </Stack>
      <Box sx={{ height: 2, mb: 0.25 }}>{loading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}</Box>
      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.15s',
        }}
      >
        <PnLTable
          columns={data.columns}
          rows={data.rows}
          headerGroups={data.headerGroups}
          height={height}
          cellHighlight={highlight}
        />
      </Box>
    </Paper>
  )
}

/** Persist date range per-user cross-session (user_preferences registry). */
interface DashboardPnlPageFilter {
  dateFrom: string | null
  dateTo: string | null
}

export default function PnLDashboardPage() {
  // Date range — persist qua user_preferences ('dashboard'/'page_filter', sub_key 'pnl').
  const filter = usePreference<DashboardPnlPageFilter>('dashboard', 'page_filter', 'pnl')
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const hydrated = useRef(false)

  // Hydrate state 1 lần từ preference (default ⊕ user delta).
  useEffect(() => {
    if (filter.isLoading || hydrated.current) return
    hydrated.current = true
    setFromDate(filter.value.dateFrom ? dayjs(filter.value.dateFrom) : null)
    setToDate(filter.value.dateTo ? dayjs(filter.value.dateTo) : null)
  }, [filter])

  // Debounced save khi user đổi date. KHÔNG cần invalidateTags: 2 date là query
  // arg của useGetPnLDashboardQuery → args đổi tự refetch.
  useEffect(() => {
    if (!hydrated.current) return
    const timer = setTimeout(() => {
      filter
        .patch({
          dateFrom: fromDate ? fromDate.format('YYYY-MM-DD') : null,
          dateTo: toDate ? toDate.format('YYYY-MM-DD') : null,
        })
        .catch(() => undefined)
    }, 450)
    return () => clearTimeout(timer)
  }, [fromDate, toDate, filter])

  // Data từ Report DB. Override `rows` của config mock bằng rows API khi có;
  // rỗng (SQL placeholder chưa điền) → fallback số dummy để card vẫn render.
  const { data, isFetching, refetch } = useGetPnLDashboardQuery({
    fromDate: fromDate?.format('YYYY-MM-DD'),
    toDate: toDate?.format('YYYY-MM-DD'),
  })

  // Ép refetch MỖI lần user đổi date — kể cả chọn lại đúng giá trị đang hiển thị
  // (vd 30-06-2026 = default ⇒ args không đổi ⇒ RTK cache hit, không tự gọi).
  // Nonce bump ở onChange → effect gọi refetch() để luôn reload block.
  const [reloadNonce, setReloadNonce] = useState(0)
  useEffect(() => {
    if (!hydrated.current || reloadNonce === 0) return
    refetch()
  }, [reloadNonce, refetch])
  const pickFrom = (d: Dayjs | null) => {
    setFromDate(d)
    setReloadNonce((n) => n + 1)
  }
  const pickTo = (d: Dayjs | null) => {
    setToDate(d)
    setReloadNonce((n) => n + 1)
  }
  const withRows = (config: PnLTableData, rows?: PnLRow[]): PnLTableData =>
    rows && rows.length ? { ...config, rows } : config

  // G) M2M PnL — ghi đè label cột `today`/`prev` theo ngày snapshot BE trả (vd "04 Jun").
  const m2mPnlData = useMemo(() => {
    const base = withRows(m2mPnlTable, data?.m2mPnl)
    if (!data?.m2mTodayDate && !data?.m2mPrevDate) return base
    const columns = base.columns.map((c) => {
      if (c.key === 'today' && data?.m2mTodayDate) return { ...c, label: dayjs(data.m2mTodayDate).format('DD MMM') }
      if (c.key === 'prev' && data?.m2mPrevDate) return { ...c, label: dayjs(data.m2mPrevDate).format('DD MMM') }
      return c
    })
    return { ...base, columns }
  }, [data])

  // F) Long-Short — header tầng giữa = tháng hiện tại (PBI dùng tháng of today), không hard-code.
  const longShortData = useMemo(() => {
    const base = withRows(longShortTable, data?.longShort)
    if (!base.headerGroups) return base
    const headerGroups = base.headerGroups.map((g, i) =>
      i === 1 ? { ...g, label: dayjs().format('YYYY-M') } : g,
    )
    return { ...base, headerGroups }
  }, [data])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
      {/* ── HEADER (doc §3): title + ngày hiện tại + date range ───────────────── */}
      <Paper
        variant="outlined"
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 1,
          borderColor: PNL_COLORS.border,
          borderRadius: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Date nằm KẾ BÊN title (1 dòng) để header thấp. */}
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 800, color: PNL_COLORS.positive, lineHeight: 1.1 }}>
            COMMERCIAL POSITION
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: PNL_COLORS.subtitle, fontWeight: 600 }}>
            {dayjs().format('MMM DD, YYYY')}
          </Typography>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Format filter copy từ filter chung Sale Contract (spacing 0.45 +
              FILTER_HEADER_INPUT_SX + label picker đầu + icon `→`). */}
          <Stack direction="row" spacing={0.45} alignItems="center" flexWrap="wrap" useFlexGap>
            <DatePicker
              label="Period"
              format="DD-MMM-YYYY"
              value={fromDate}
              onChange={pickFrom}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: FILTER_HEADER_INPUT_SX,
                  InputLabelProps: { shrink: true },
                  inputProps: { 'aria-label': 'Period start date' },
                },
              }}
            />
            <ArrowRightAltIcon
              fontSize="small"
              sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
              aria-hidden="true"
            />
            <DatePicker
              format="DD-MMM-YYYY"
              value={toDate}
              onChange={pickTo}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: FILTER_HEADER_INPUT_SX,
                  inputProps: { 'aria-label': 'Period end date' },
                },
              }}
            />
          </Stack>
        </LocalizationProvider>
        {/* Reload toàn dashboard — refetch() ép gọi lại kể cả khi args không đổi
            (style nút theo BatchReportPage). */}
        <Tooltip title="Reload dashboard data">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Reload
            </Button>
          </span>
        </Tooltip>
      </Paper>

      {/* ── GRID 3 CỘT — phân bổ theo layout .pbix page "Dashboard" (decode
          visual position): cột trái x30 w608 / giữa x663 w652 / phải x1344 w535
          → tỉ lệ 1.14 : 1.22 : 1 (cột phải HẸP nhất). Mobile stack + scroll
          ngang bảng. Reload theo TỪNG block: mỗi PnLCard tự show progress + mờ
          bảng của nó khi `isFetching`, KHÔNG dim cả grid/page. ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.14fr) minmax(0,1.22fr) minmax(0,1fr)' },
          gap: 1,
          alignItems: 'start',
        }}
      >
        {/* ── HÀNG TRÊN (PBI y≈130–680) ── */}
        {/* CỘT 1 */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <PnLCard title="Purchases" unit="(Kg)" data={withRows(purchasesTable, data?.purchases)} loading={isFetching} />
          <PnLCard title="Sales" unit="(Kg)" data={withRows(salesTable, data?.sales)} loading={isFetching} />
          {/* Bảng dài scroll dọc trong card (sticky header). */}
          {/* height chỉnh để đáy CỘT 1 ngang đáy card Supplier's M2M (CỘT 2). */}
          <PnLCard title="Futures" unit="(Lot)" data={withRows(futuresTable, data?.futures)} height={140} loading={isFetching} />
        </Stack>

        {/* CỘT 2 — bảng dài: cap height + scroll dọc trong card (sticky header). */}
        <Box sx={{ minWidth: 0 }}>
          {/* KHÔNG cell-highlight nền đỏ: PBI chỉ CF fontColor theo value
              (đã cover bởi fmtNum số âm = ngoặc đỏ) — crosscheck 2026-06-10. */}
          <PnLCard
            title="Supplier's M2M Exposure"
            unit="(USD)"
            data={withRows(supplierM2MTable, data?.supplierM2M)}
            height={420}
            loading={isFetching}
          />
        </Box>

        {/* CỘT 3 — PBI: Physical price (w322) + Exchange rate (w208) nằm CẠNH
            NHAU cùng hàng y485 (tỉ lệ 1.55 : 1), KHÔNG stack dọc. xs stack. */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <PnLCard title="M2M PnL" unit="(USD)" data={m2mPnlData} loading={isFetching} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1.55fr) minmax(0,1fr)' },
              gap: 1,
              alignItems: 'start',
            }}
          >
            <PnLCard title="Physical price" data={withRows(physicalPriceTable, data?.physicalPrice)} loading={isFetching} />
            <PnLCard title="Exchange rate" data={withRows(exchangeRateTable, data?.exchangeRate)} loading={isFetching} />
          </Box>
        </Stack>

        {/* ── HÀNG DƯỚI (PBI y≈694): Inventory / Long-Short / Market price
            CÙNG 1 HÀNG — grid row riêng nên top 3 card luôn thẳng hàng. ── */}
        {/* 3 block CÙNG height (chị Đẹp yêu cầu) — bảng dài hơn thì scroll. */}
        <Box sx={{ minWidth: 0 }}>
          <PnLCard title="Inventory" unit="(Kg)" data={withRows(inventoryTable, data?.inventory)} height={235} loading={isFetching} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <PnLCard title="Long-Short" unit="(Kg)" data={longShortData} height={235} loading={isFetching} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <PnLCard title="Market price" data={withRows(marketPriceTable, data?.marketPrice)} height={235} loading={isFetching} />
        </Box>
      </Box>
    </Box>
  )
}
